import * as React from 'react';
import { TitleText } from '../cookbook-ui/custom-components/TitleText/TitleText';
import { Page } from '../cookbook-ui/Page_SC';
import { ExportTemplate } from './ComponentTemplate/ComponentTemplate';
import { AllCategory, documentation, isComponent } from '../utils/Documentation';
import { useLocation } from 'react-router-dom';

interface Props {
  category: AllCategory;
}

export const Category = React.memo((props: Props): JSX.Element | null => {
  const { category } = props;

  const categoryData = React.useMemo(() => {
    const data = [...(documentation.byAllCategory[category] ?? [])];

    // Reorder so that each component is followed by its props and events (Component -> Props -> Events)
    // Preserve order of other elements (no simple swapping)
    for (let i = 0; i < data.length; i += 1) {
      if (isComponent(data[i])) {
        const { props, events } = documentation.byComponent[data[i].name];
        // Move events first so that props is inserted BEFORE events
        const eventsIndex = data.findIndex((d) => d.name === events.name);
        if (eventsIndex >= 0) {
          const [events] = data.splice(eventsIndex, 1);
          data.splice(i + 1, 0, events);
        }
        const propsIndex = data.findIndex((d) => d.name === props.name);
        if (propsIndex >= 0) {
          const [props] = data.splice(propsIndex, 1);
          data.splice(i + 1, 0, props);
        }
      }
    }

    if (category === 'web-components') {
      return data.filter((e) => e.kind !== 'class' || !e.name.endsWith('Component'));
    }

    return data;
  }, [category]);

  const mapped = React.useMemo(() => {
    const included = categoryData.filter((e) => !e.name.startsWith('_'));
    if (!included || included.length === 0) {
      return null;
    }

    return included.map((e) => <ExportTemplate data={e} />);
  }, [categoryData]);

  const location = useLocation();
  React.useEffect(() => {
    location.hash && document.querySelector(location.hash)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [location.hash]);

  return categoryData ? (
    <Page id={category}>
      <TitleText headerText={category} footerText="" isLargeHeader />
      <hr />
      {mapped}
    </Page>
  ) : null;
});
